import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useCallback, useState } from 'react';

import { RequestActivateQrRequest } from '../../shared/api/hscan/qrCode';
import { ErrorResponse } from '../api/hscan';
import {
  Case,
  DicomDownloadEvent,
  GenerateHpacsLinkBody,
  HpacsLinkResponse,
} from '../api/hscan/case';
import {
  requestActivateQr,
  requestQrCase,
  requestQrDicomImage,
  requestQrDownloadImage,
  generateQrHpacsLinkId,
} from '../api/hscan/qrCode';
import { HPACS_URL, HSCAN_BACKEND_API_URL } from '../env';

import {
  DownloadOptions,
  DownloadProgressEventHandler,
  useDownloadFile,
} from './caseHook';
import { MutationOption, QueryOption } from './react-query-type';

export const useActivateQr = (
  options?: MutationOption<RequestActivateQrRequest, void>,
) => {
  return useMutation({
    mutationFn: async params => {
      await requestActivateQr(params);
    },
    ...options,
  });
};

export const useQrCase = (sessionId: string, options?: QueryOption<Case[]>) => {
  return useQuery({
    queryKey: ['QR', sessionId, 'Case'],
    queryFn: async () => {
      const { data } = await requestQrCase(sessionId);
      return [data];
    },
    ...options,
  });
};

export const useQrDicomImage = (imageId: string, sessionId: string) => {
  const fetch = useCallback(async () => {
    const { data } = await requestQrDicomImage({ imageId, sessionId });
    return data;
  }, [imageId, sessionId]);
  return useQuery({
    queryKey: ['requestDicomImage', imageId],
    queryFn: fetch,
    staleTime: 1000 * 3600 * 24,
  });
};

export const useQrLink = (
  sessionId: string,
  options?: MutationOption<
    GenerateHpacsLinkBody,
    HpacsLinkResponse,
    AxiosError<ErrorResponse>
  >,
) => {
  return useMutation({
    mutationKey: [],
    mutationFn: async body => {
      const { startDtime, endDtime } = body;
      const now = startDtime ?? new Date().toISOString();
      const expire =
        endDtime ?? new Date(new Date().getTime() + 86400000).toISOString();
      const { data } = await generateQrHpacsLinkId({
        body: { ...body, startDtime: now, endDtime: expire },
        sessionId,
      });
      return data;
    },
    ...options,
  });
};

// API hook 이 아님
export const useGetQrLinkUrl = (sessionId: string) => {
  const { mutateAsync: requestQrLink } = useQrLink(sessionId, {
    onError(error) {
      console.warn(error.response?.status, error.response?.data);
    },
  });

  return useCallback(
    async (caseIds: string[]) => {
      const link = await requestQrLink({ caseId: caseIds });
      return `${HPACS_URL}/linkLogin/${link?.id}`;
    },
    [requestQrLink],
  );
};

/**
 * 다운로드
 */
// TODO: 이것도 3번이나 반복돼서 hook 으로 빼야함
export const useQrSubscribeDownloadProgress = (
  sseKey: string,
  downloadProgressEventHandler: DownloadProgressEventHandler,
  sessionId: string,
) => {
  const [progress, setProgress] = useState<number>(0);
  const [currentCase, setCurrentCase] = useState<number>(1);
  const [eventSource, setEventSource] = useState<EventSourcePolyfill | null>(
    null,
  );
  const subscribeDownloadProgress = () => {
    const eventSource = new EventSourcePolyfill(
      `${HSCAN_BACKEND_API_URL}/qr/case/dicom/sse/${sseKey}/event`,
      {
        heartbeatTimeout: 60 * 60 * 1000,
        withCredentials: true,
        headers: {
          SESSIONID: sessionId,
        },
      },
    );
    let progress = 0;
    let currentCase = 1;
    eventSource.onmessage = ev => {
      progress = progress + 1;
      const eventData = JSON.parse(ev.data) as DicomDownloadEvent;
      if (currentCase && currentCase !== eventData.completeCount) {
        currentCase = currentCase + 1;
        progress = 0;
      }
      if (eventData.eventId === 'DOWNLOAD') {
        downloadProgressEventHandler.onProgress({
          progress,
          totalImageCount: eventData.totalImageCount,
          currentCase,
          totalCaseCount: eventData.totalCount,
        });
      } else if (eventData.eventId === 'COMPRESS') {
        downloadProgressEventHandler.onCompress();
      }
      if (
        eventData.eventId === 'COMPLETE' ||
        (eventData.eventId === 'COMPRESS' && // COMPLETE가 오지 않을 때를 방지하기 위한 로직
          eventData.completeCount === eventData.totalCount &&
          progress === eventData.totalImageCount)
      ) {
        downloadProgressEventHandler.onDownload();
        eventSource.close();
      }
    };

    eventSource.onerror = _ => {
      eventSource.close();
      progress = 0;
      currentCase = 1;
    };
  };

  return { subscribeDownloadProgress };
};

export const useQrDownloadImage = (
  caseIds: string[],
  sseKey: string | undefined,
  sessionId: string,
  options?: DownloadOptions,
) => {
  // TODO: useDownloadFile 에서 header 정보를 사용하고 있어서 fetchData 타입을 AxiosResponse 로 유지함
  const fetchData = useCallback(async () => {
    if (sseKey) {
      return requestQrDownloadImage({ sseKey, sessionId, caseIds });
    }
  }, [caseIds, sseKey, sessionId]);
  return useDownloadFile(fetchData, options);
};
